import { initializeApp } from "firebase/app";
import { getAuth, setPersistence, browserLocalPersistence } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getAnalytics, isSupported } from "firebase/analytics";

// Validação de variáveis de ambiente obrigatórias
const requiredEnvVars = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  'REACT_APP_FIREBASE_APP_ID',
] as const;

const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);
const isDevelopment = process.env.NODE_ENV === 'development';

// Modo de desenvolvimento sem Firebase (permite trabalhar sem credenciais)
if (missingVars.length > 0) {
  const warningMsg = `
╔════════════════════════════════════════════════════════════════╗
║  ⚠️  FIREBASE CONFIGURATION MISSING                            ║
╠════════════════════════════════════════════════════════════════╣
║  Variáveis de ambiente não encontradas:                        ║
║  ${missingVars.map(v => `• ${v}`).join('\n║  ')}
║                                                                ║
║  ${isDevelopment ? '🔧 MODO DESENVOLVIMENTO ATIVO' : '⛔ PRODUÇÃO BLOQUEADA'}                            ║
║  ${isDevelopment ? 'Usando configuração MOCK para desenvolvimento.' : 'Configure Firebase para produção!'}       ║
║                                                                ║
║  📝 PARA USAR FIREBASE REAL:                                  ║
║  1. Crie um arquivo .env.local na raiz do projeto             ║
║  2. Copie o template de .env.example                          ║
║  3. Adicione suas credenciais do Firebase                     ║
║                                                                ║
║  🔗 Obtenha suas credenciais em:                              ║
║  https://console.firebase.google.com/                         ║
╚════════════════════════════════════════════════════════════════╝
  `.trim();

  if (isDevelopment) {
    console.warn(warningMsg);
  } else {
    console.error(warningMsg);
    throw new Error('Firebase configuration is required in production.');
  }
}

// Configuração real ou mock (para desenvolvimento)
const firebaseConfig = missingVars.length > 0 && isDevelopment
  ? {
      // Configuração MOCK apenas para desenvolvimento
      apiKey: 'mock-api-key-for-development',
      authDomain: 'mock-project.firebaseapp.com',
      projectId: 'mock-project-dev',
      storageBucket: 'mock-project-dev.appspot.com',
      messagingSenderId: '000000000000',
      appId: '1:000000000000:web:mock-app-id',
      measurementId: 'G-MOCK00000',
    }
  : {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY!,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN!,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID!,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET!,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID!,
      appId: process.env.REACT_APP_FIREBASE_APP_ID!,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
    };

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Flag indicando se está usando configuração mock
export const isUsingMockConfig = missingVars.length > 0 && isDevelopment;

// Aviso visual no console quando usando mock
if (isUsingMockConfig) {
  console.warn(
    '%c🔧 FIREBASE MOCK MODE ATIVO',
    'background: #ff9800; color: white; font-weight: bold; padding: 8px; border-radius: 4px;',
    '\nA aplicação está rodando com configuração MOCK.\nAutenticação e dados NÃO serão persistidos.\n\nPara usar Firebase real, configure .env.local'
  );
}

// Configuração específica para melhorar compatibilidade com mobile
auth.useDeviceLanguage();

// Garantir persistência local, especialmente importante no mobile
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.warn('[FIREBASE] Erro ao configurar persistência:', error);
});

// Analytics só no navegador (evita erro em SSR ou testes)
export let analytics: ReturnType<typeof getAnalytics> | undefined = undefined;
if (typeof window !== "undefined" && window.location.hostname === 'localhost') {
  isSupported().then((yes) => {
    if (yes) analytics = getAnalytics(app);
  });
}